import { useEffect, useState, useCallback } from 'react';

// ----------------------------------------------------------------------

export function useLocalStorage(key: string, initialState: unknown) {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    const restored = getStorage(key);

    if (restored) {
      setState((prevValue: unknown) => ({
        ...(prevValue as Record<string, unknown>),
        ...(restored as Record<string, unknown>),
      }));
    }
  }, [key]);

  const updateState = useCallback(
    (updateValue: Record<string, unknown>) => {
      setState((prevValue: unknown) => {
        const prev = prevValue as Record<string, unknown>;
        setStorage(key, {
          ...prev,
          ...updateValue,
        });

        return {
          ...prev,
          ...updateValue,
        };
      });
    },
    [key],
  );

  const update = useCallback(
    (name: string, updateValue: unknown) => {
      updateState({
        [name]: updateValue,
      });
    },
    [updateState],
  );

  const reset = useCallback(() => {
    removeStorage(key);
    setState(initialState);
  }, [initialState, key]);

  return {
    state,
    update,
    reset,
  };
}

// ----------------------------------------------------------------------

export const getStorage = (key: string) => {
  let value = null;

  try {
    const result = window.localStorage.getItem(key);

    if (result) {
      value = JSON.parse(result);
    }
  } catch (e) {
    console.error(e);
  }

  return value;
};

export const setStorage = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(e);
  }
};

export const removeStorage = (key: string) => {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    console.error(e);
  }
};
